import  { useEffect, useState } from "react";
import { FaUser } from "react-icons/fa";
// import { IoCloseOutline } from "react-icons/io5";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  CustomerLga,
  CustomerStates,
} from "../../../utils/redux/Auth/AuthSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axiosServicesOnboarding from "../../../services/axiosServicesOnboarding";
import { Box, Typography } from "@mui/material";

const StepBox = ({ number, title }) => {
  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        border="1px solid #e0e0e0"
        borderRadius="8px"
        padding="6px"
        bgcolor="#ffffff"
        position="relative"
      >
        {/* Number Circle */}
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="40px"
          height="40px"
          border="2px solid #001d7e"
          borderRadius="50%"
          marginRight="8px"
        >
          <Typography variant="body1" color="#001d7e" fontWeight="bold">
            {number}
          </Typography>
        </Box>

        {/* Title */}
        <Typography variant="body1" fontWeight="bold" color="#001d7e">
          {title}
        </Typography>

        {/* Pointed edge - border */}
        <Box
          position="absolute"
          right="-21px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "21px solid #e0e0e0",
            borderTop: "21px solid transparent",
            borderBottom: "21px solid transparent",
          }}
        />

        {/* Pointed edge - fill */}
        <Box
          position="absolute"
          right="-20px"
          top="50%"
          sx={{
            transform: "translateY(-50%)",
            borderLeft: "20px solid #ffffff",
            borderTop: "20px solid transparent",
            borderBottom: "20px solid transparent",
            zIndex: 1,
            height: "60px",
          }}
        />
      </Box>
    </Box>
  );
};

const AddressVerification = ({ onBack, refresh, setRefresh }) => {
  const dispatch = useDispatch();
  const allStates = useSelector((state) => state?.auth?.customer_states || []);
  const allLga = useSelector((state) => state?.auth?.customer_lga || []);
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object({
    address: Yup.string().required("Address is required"),
    state: Yup.string().required("State is required"),
    lga: Yup.string().required("LGA is required"),
  });

  // Fetch states on component mount
  useEffect(() => {
    dispatch(CustomerStates());
  }, [dispatch]);

  const handleStateChange = async (selectedState, formikSetFieldValue) => {
    formikSetFieldValue("state", selectedState);
    formikSetFieldValue("lga", ""); // Reset LGA when state changes
    if (selectedState) {
      setLoading(true);
      await dispatch(CustomerLga(selectedState));
      setLoading(false);
    }
  };

  const handleError = (error) => {
    const errorMessages = error?.response?.data?.errors;

    if (errorMessages) {
      Object.keys(errorMessages).forEach((field) => {
        // Display error messages in the console
        toast.error(`${field}: ${errorMessages[field].join(", ")}`);
      });
    } else {
      console.log("An unexpected error occurred.");
    }
  };

  return (
    <div className="w-full md:w-[650px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 text-gray-700 text-lg font-semibold">
          <FaUser className="text-xl text-primary-color" />
          <span>Add Address</span>
        </div>
        <button onClick={onBack} className="text-gray-600 text-2xl">
          {/* <IoCloseOutline /> */}
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.0003 29.3337C23.3641 29.3337 29.3337 23.3641 29.3337 16.0003C29.3337 8.63653 23.3641 2.66699 16.0003 2.66699C8.63653 2.66699 2.66699 8.63653 2.66699 16.0003C2.66699 23.3641 8.63653 29.3337 16.0003 29.3337Z"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M20 12L12 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 12L20 20"
              stroke="#25233A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="px-8 py-1 bg-[#edf1f9] rounded-md mb-8">
        <Box maxWidth="600px" width="100%" p={2}>
          <StepBox number="04" title="Add Address" />
        </Box>
      </div>

      <h3 className="text-lg font-semibold text-primary-color text-center mb-6">
        Provide Address
      </h3>

      <Formik
        initialValues={{ address: "", state: "", lga: "" }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          // Handle address submission logic here
          const payload = {
            businessAddress: values.address,
            state: values.state,
            lga: values.lga,
          };
          try {
            setLoading(true);
            const response = await axiosServicesOnboarding.post(
              "Customer/create-address",
              payload
            );
            console.log("Response", response);
            if (response?.status === true) {
              // localStorage.setItem(
              //   "onboardingStatus",
              //   JSON.stringify("PIN SETUP")
              // );
              setLoading(false);
              toast.success("Address added successfully");
              setRefresh(!refresh);
            } else {
              console.log(response);
              toast.error(response?.payload?.data?.message);
              setLoading(false);
            }
          } catch (error) {
            handleError(error);
            setLoading(false);
            // toast.error("An error occurred. Please try again later");
          }
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className="space-y-6 w-full md:w-[400px] mx-auto">
            <div>
              <label className="block text-gray-600 font-medium mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <Field
                type="text"
                name="address"
                placeholder="47, Yakubu Gowon, Street. Eyin Eyo Lagos Island"
                className="w-full p-3 border border-gray-300 rounded-md"
              />
              <ErrorMessage
                name="address"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            <div>
              <label className="block text-gray-600 font-medium mb-2">
                Select State <span className="text-red-500">*</span>
              </label>
              <Field
                as="select"
                name="state"
                className="w-full p-3 border border-gray-300 rounded-md"
                onChange={(e) =>
                  handleStateChange(e.target.value, setFieldValue)
                }
              >
                <option value="">Select State</option>
                {allStates.map((state) => (
                  <option key={state?.id} value={state?.id}>
                    {state?.name}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="state"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            <div>
              <label className="block text-gray-600 font-medium mb-2">
                Select LGA <span className="text-red-500">*</span>
              </label>
              <Field
                as="select"
                name="lga"
                className="w-full p-3 border border-gray-300 rounded-md"
                disabled={loading || allLga.length === 0}
              >
                <option value="">Select LGA</option>
                {allLga.map((lga) => (
                  <option key={lga?.id} value={lga?.id}>
                    {lga?.name}
                  </option>
                ))}
              </Field>
              <ErrorMessage
                name="lga"
                component="div"
                className="text-red-500 text-sm mt-1"
              />
            </div>

            {loading && (
              <p className="text-sm text-gray-500 mt-2 text-center">
                Loading LGAs, please wait...
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary-color hover:bg-[#8388B6] text-white font-semibold py-4 px-6 rounded-md"
            >
              {isSubmitting ? "Submitting..." : "Next"}
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddressVerification;
